import { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { supabase } from '../../lib/supabase';
import { formatDate } from '../../utils/dateUtils';
import DeleteConfirmationModal from '../../components/clientDetail/sections/functions/DeleteConfirmationModal';
import { Button, Badge, Card, Dropdown, Icon, IconButton, SidePanel } from '../../components/ui';
import type { BadgeTone } from '../../components/ui';

interface LinkQueueItem {
  id: string;
  client_id: string;
  client_name: string;
  provider: 'Local Warden' | 'Pancake' | 'Bullfinch';
  website_id: string;
  website_url: string;
  website_title: string | null;
  status: 'pending' | 'processing' | 'completed' | 'cancelled';
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface Client {
  id: string;
  name: string;
}

interface Website {
  id: string;
  url: string;
  title: string | null;
}

const STATUS_TONES: Record<string, BadgeTone> = {
  pending: 'info',
  processing: 'warn',
  completed: 'up',
  cancelled: 'down',
};

export default function LinkQueuePanel() {
  const { user } = useUser();
  const [queueItems, setQueueItems] = useState<LinkQueueItem[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [websites, setWebsites] = useState<Website[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [newItem, setNewItem] = useState({
    client_id: '',
    provider: 'Local Warden' as LinkQueueItem['provider'],
    website_id: '',
    notes: ''
  });

  useEffect(() => {
    fetchQueueItems();
    fetchClients();
    fetchWebsites();
  }, []);

  const fetchQueueItems = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('link_queue')
        .select(`
          *,
          clients!inner(name),
          websites!inner(url, title)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      type JoinedRow = LinkQueueItem & { clients: { name: string }; websites: { url: string; title: string | null } };
      const transformedItems = ((data || []) as unknown as JoinedRow[]).map((item) => ({
        ...item,
        client_name: item.clients.name,
        website_url: item.websites.url,
        website_title: item.websites.title
      }));

      setQueueItems(transformedItems);
    } catch (err) {
      console.error('Error fetching link queue items:', err);
      setError('Failed to load link queue. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setClients(data || []);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const fetchWebsites = async () => {
    try {
      const { data, error } = await supabase
        .from('websites')
        .select('id, url, title')
        .order('url');

      if (error) throw error;
      setWebsites(data || []);
    } catch (err) {
      console.error('Error fetching websites:', err);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.client_id || !newItem.website_id) {
      setError('Client and website are required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('link_queue')
        .insert({
          client_id: newItem.client_id,
          provider: newItem.provider,
          website_id: newItem.website_id,
          notes: newItem.notes || null,
          created_by: user?.id
        });

      if (error) throw error;

      setSuccess('Link queue item added successfully!');
      setNewItem({
        client_id: '',
        provider: 'Local Warden',
        website_id: '',
        notes: ''
      });
      setIsAddingItem(false);
      fetchQueueItems();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error adding link queue item:', err);
      setError('Failed to add item to link queue. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusChange = async (itemId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('link_queue')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', itemId);

      if (error) throw error;

      setQueueItems(items =>
        items.map(item =>
          item.id === itemId
            ? { ...item, status: newStatus as LinkQueueItem['status'], updated_at: new Date().toISOString() }
            : item
        )
      );
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status.');
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    setIsDeleting(true);

    try {
      const { error } = await supabase
        .from('link_queue')
        .delete()
        .eq('id', itemToDelete);

      if (error) throw error;

      setQueueItems(items => items.filter(item => item.id !== itemToDelete));
      setSuccess('Link queue item deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting link queue item:', err);
      setError('Failed to delete item. Please try again.');
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const formatUrl = (url: string) => {
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  };

  const pendingCount = queueItems.filter(i => i.status === 'pending').length;

  return (
    <div>
      {/* Status Messages */}
      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-4)',
            padding: '10px 14px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-down-soft)',
            border: '1px solid rgba(229, 72, 77, 0.25)',
            color: 'var(--danger-400)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <span style={{ flex: 1 }}>{error}</span>
          <button
            onClick={() => setError(null)}
            className="pressable hover-underline"
            style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-ui)' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: 'var(--space-4)',
            padding: '10px 14px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-up-soft)',
            border: '1px solid rgba(86, 162, 104, 0.25)',
            color: 'var(--status-up)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <Icon name="check" size={14} />
          {success}
        </div>
      )}

      <Card
        eyebrow="Link Queue"
        title={`${pendingCount} awaiting approval`}
        actions={
          <Button size="sm" icon="plus" onClick={() => setIsAddingItem(true)}>
            Add to Queue
          </Button>
        }
      >
        {/* Queue Items */}
        {isLoading ? (
          <div className="flex justify-center items-center" style={{ padding: 'var(--space-14) 0' }}>
            <div
              className="w-8 h-8 rounded-full animate-spin"
              style={{ border: '3px solid var(--border-hairline)', borderTopColor: 'var(--accent)' }}
            />
          </div>
        ) : queueItems.length === 0 ? (
          <div className="text-center" style={{ padding: 'var(--space-10) 0' }}>
            <div style={{ color: 'var(--text-faint)', marginBottom: 'var(--space-3)' }}>
              <Icon name="link" size={28} />
            </div>
            <h3
              style={{
                fontSize: 'var(--text-md)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                color: 'var(--text-body)',
                marginBottom: 'var(--space-2)',
              }}
            >
              No Items in Queue
            </h3>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 'var(--space-5)' }}>
              Add websites to the queue for linking.
            </p>
            <Button size="sm" icon="plus" onClick={() => setIsAddingItem(true)}>
              Add to Queue
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {queueItems.map((item) => (
              <div
                key={item.id}
                className="hover-wash flex flex-col md:flex-row md:items-center"
                style={{
                  gap: '12px',
                  padding: '10px',
                  margin: '0 -10px',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                {/* source → target */}
                {/* Wraps on phones so the target URL and note aren't cut to nothing */}
                <div className="flex items-center flex-1 flex-wrap md:flex-nowrap" style={{ gap: '4px 10px', minWidth: 0 }}>
                  <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', whiteSpace: 'nowrap' }}>
                    {item.client_name}
                  </span>
                  <span style={{ color: 'var(--text-faint)', display: 'inline-flex', flexShrink: 0 }}>
                    <Icon name="arrow-right" size={13} />
                  </span>
                  <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formatUrl(item.website_url)}
                  </span>
                  {item.notes && (
                    <span
                      style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--text-faint)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={item.notes}
                    >
                      "{item.notes}"
                    </span>
                  )}
                </div>

                <div className="flex items-center flex-wrap md:flex-nowrap" style={{ gap: '10px' }}>
                  <Badge tone="neutral">{item.provider}</Badge>

                  <span className="t-num hidden lg:inline" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>
                    {formatDate(item.created_at, 'MMM d')}
                  </span>

                  <Badge tone={STATUS_TONES[item.status] || 'idle'} dot>
                    {item.status}
                  </Badge>

                  <Dropdown
                    size="sm"
                    options={[
                      { value: 'pending', label: 'Pending' },
                      { value: 'processing', label: 'Processing' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'cancelled', label: 'Cancelled' }
                    ]}
                    value={item.status}
                    onChange={(e) => handleStatusChange(item.id, e.target.value)}
                    placeholder="Status..."
                    style={{ width: '128px' }}
                  />

                  <IconButton
                    icon="trash-2"
                    size="sm"
                    label="Delete Item"
                    onClick={() => {
                      setItemToDelete(item.id);
                      setDeleteModalOpen(true);
                    }}
                    style={{ color: 'var(--danger-400)' }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Queue Statistics */}
      {queueItems.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4" style={{ gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
          {[
            { label: 'Pending', count: queueItems.filter(i => i.status === 'pending').length, color: 'var(--status-info)' },
            { label: 'Processing', count: queueItems.filter(i => i.status === 'processing').length, color: 'var(--status-warn)' },
            { label: 'Completed', count: queueItems.filter(i => i.status === 'completed').length, color: 'var(--status-up)' },
            { label: 'Cancelled', count: queueItems.filter(i => i.status === 'cancelled').length, color: 'var(--status-down)' }
          ].map((stat) => (
            <div
              key={stat.label}
              style={{
                background: 'var(--surface-card)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-md)',
                padding: '10px 12px',
              }}
            >
              <div className="t-eyebrow" style={{ marginBottom: '4px' }}>{stat.label}</div>
              <div
                className="t-num"
                style={{
                  fontSize: 'var(--text-md)',
                  fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                  color: stat.color,
                }}
              >
                {stat.count}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Item Panel */}
      <SidePanel
        open={isAddingItem}
        onClose={() => setIsAddingItem(false)}
        width={420}
        eyebrow="Link Queue"
        title="Add to Queue"
        footer={
          <>
            <Button
              icon="check"
              onClick={handleAddItem}
              disabled={isSubmitting || !newItem.client_id || !newItem.website_id}
            >
              {isSubmitting ? 'Adding...' : 'Add to Queue'}
            </Button>
            <Button variant="ghost" onClick={() => setIsAddingItem(false)} disabled={isSubmitting}>
              Cancel
            </Button>
          </>
        }
      >
        <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <Dropdown
            label="Client*"
            options={clients.map(client => ({ value: client.id, label: client.name }))}
            value={newItem.client_id}
            onChange={(e) => setNewItem({ ...newItem, client_id: e.target.value })}
            placeholder="Select client..."
          />

          <Dropdown
            label="Provider*"
            options={[
              { value: 'Local Warden', label: 'Local Warden' },
              { value: 'Pancake', label: 'Pancake' },
              { value: 'Bullfinch', label: 'Bullfinch' }
            ]}
            value={newItem.provider}
            onChange={(e) => setNewItem({ ...newItem, provider: e.target.value as LinkQueueItem['provider'] })}
            placeholder="Select provider..."
          />

          <Dropdown
            label="Website*"
            options={[...websites]
              .sort((a, b) => (a.title || formatUrl(a.url)).toLowerCase().localeCompare((b.title || formatUrl(b.url)).toLowerCase()))
              .map(website => ({
                value: website.id,
                label: website.title || formatUrl(website.url)
              }))}
            value={newItem.website_id}
            onChange={(e) => setNewItem({ ...newItem, website_id: e.target.value })}
            placeholder="Select website..."
          />

          <div>
            <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Notes</div>
            <textarea
              className="ck-textarea"
              value={newItem.notes}
              onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
              rows={3}
              placeholder="Additional notes about this link request..."
            />
          </div>
        </div>
      </SidePanel>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Link Queue Item"
        message="Are you sure you want to delete this link queue item? This action cannot be undone."
        isDeleting={isDeleting}
      />
    </div>
  );
}
